import { useCallback, useEffect, useState } from 'react';

import { Alert } from '@zooplus/ui-components';

import LocalPaymentsRetryInitiator from '../LocalPaymentsRetryInitiator/LocalPaymentsRetryInitiator';
import RetryPaymentApplePay from '../RetryPaymentApplePay/RetryPaymentApplePay';
import RetryPaymentDefault from '../RetryPaymentDefault/RetryPaymentDefault';
import RetryPaymentIdeal from '../RetryPaymentIdeal/RetryPaymentIdeal';
import RetryPaymentNotification from '../RetryPaymentNotification/RetryPaymentNotification';
import RetryPaymentPaypal from '../RetryPaymentPaypal/RetryPaymentPaypal';

import { useAppContext } from '@providers/AppContextProvider';
import { useKeycloakTokenProvider } from '@providers/KeycloakTokenProvider';
import { useRetryState } from '@providers/RetryDataProvider';
import RetryPaymentContextProvider, {
  useRetryPaymentContext,
} from '@providers/RetryPaymentContextProvider';
import { useThunkDispatch } from '@providers/ThunkDispatchProvider';
import { getRetryData } from '@services/retryPaymentService';
import { FraudPreventionRecommendation, PspConnector, RetryPaymentMethod } from '@types-internal';
import { isReturnUrlFromAdyen } from '@utils/retryUtils';

export type RetryPaymentSwitchProps = {
  orderId: string;
  customerId: string;
};

const RetryPaymentSwitch: React.FC<RetryPaymentSwitchProps> = ({ orderId, customerId }) => {
  const {
    retryState: { retryData: retryPaymentData },
  } = useRetryState();

  const { shopBundle } = useAppContext();
  const [errorMessage, setErrorMessage] = useState<string>();
  const { thunkDispatch } = useThunkDispatch();

  useEffect(() => {
    const url = window.location.href;

    const isAuthorizationResult = isReturnUrlFromAdyen(url);
    thunkDispatch(
      getRetryData({ orderId, customerId, shopBundle, isAuthorizationResult, pspResponseUrl: url }),
    );
  }, [orderId, customerId, shopBundle]);

  const handleError = useCallback((errorMessage: string) => {
    setErrorMessage(errorMessage);
  }, []);

  if (errorMessage) {
    return (
      <Alert variant="warning" data-zta="errorPaymentRetry">
        {errorMessage}
      </Alert>
    );
  }

  if (!retryPaymentData) {
    return null;
  }

  const {
    idealAvailable,
    retryPossible,
    retryPaymentMethod,
    retryPaymentURL,
    retryPaymentStatus,
    currency,
    amount,
    siteId,
    connector,
    pspToken,
    fraudPreventionRecommendation,
  } = retryPaymentData;

  return (
    <RetryPaymentContextProvider
      retryPossible={retryPossible}
      retryPaymentStatus={retryPaymentStatus}>
      <RetryPaymentSwitchWrapped
        currency={currency}
        orderId={orderId}
        customerId={customerId}
        idealAvailable={idealAvailable}
        retryPaymentMethod={retryPaymentMethod}
        retryPaymentUrl={retryPaymentURL}
        amount={amount}
        siteId={siteId}
        connector={connector}
        pspToken={pspToken}
        onError={handleError}
        fraudPreventionRecommendation={fraudPreventionRecommendation}
      />
    </RetryPaymentContextProvider>
  );
};

type RetryPaymentSwitchWrappedProps = {
  currency: string;
  orderId: string;
  customerId: string;
  idealAvailable: boolean;
  retryPaymentMethod: RetryPaymentMethod;
  retryPaymentUrl?: string;
  amount: number;
  siteId: number;
  connector: PspConnector;
  pspToken?: string;
  fraudPreventionRecommendation?: FraudPreventionRecommendation;
  onError: (errorMessage: string) => void;
};

export const RetryPaymentSwitchWrapped: React.FC<RetryPaymentSwitchWrappedProps> = ({
  currency,
  orderId,
  customerId,
  idealAvailable,
  retryPaymentMethod,
  retryPaymentUrl,
  amount,
  siteId,
  connector,
  pspToken,
  onError,
  fraudPreventionRecommendation,
}) => {
  const {
    retryPaymentInfo: { retryPossible, retryPaymentStatus },
  } = useRetryPaymentContext();
  const { tokenPayload } = useKeycloakTokenProvider() || {};
  const { sid } = tokenPayload || {};

  const isUseNewFlow = [
    'DOTPAY',
    'ONLINE_BANKING_PL',
    'PAYTRAIL',
    'BANCONTACT_MOBILE',
    'TWINT',
    'KLARNA_PAY_LATER',
    'KLARNA_PAY_OVER_TIME',
  ].some((paymentMethod) => paymentMethod === retryPaymentMethod);

  const isAdyenIdealRetry = idealAvailable && retryPossible && connector === 'ADYEN';
  const isIdealRetry = idealAvailable && retryPossible && !isAdyenIdealRetry;
  const isPaypalRetry = retryPaymentMethod === 'PAYPAL' && retryPossible;
  const isApplePayRetry = retryPaymentMethod === 'APPLE_PAY' && retryPossible;
  const isDefaultRetry =
    !isUseNewFlow &&
    !idealAvailable &&
    retryPaymentMethod !== 'PAYPAL' &&
    retryPaymentMethod !== 'APPLE_PAY' &&
    retryPossible;

  return (
    <>
      {!isUseNewFlow && retryPaymentStatus && (
        <RetryPaymentNotification
          retryPaymentStatus={retryPaymentStatus}
          retryPaymentMethod={retryPaymentMethod}
          sid={sid}
          currency={currency}
          siteId={siteId}
          customerId={customerId}
          orderId={orderId}
        />
      )}
      {(isUseNewFlow || isAdyenIdealRetry) && (
        <LocalPaymentsRetryInitiator
          orderId={orderId}
          customerId={customerId}
          isRetryPossible={retryPossible}
          retryPaymentMethod={retryPaymentMethod}
          sid={sid}
          currency={currency}
          siteId={siteId}
        />
      )}

      {isIdealRetry && (
        <RetryPaymentIdeal onError={onError} orderId={orderId} customerId={customerId} />
      )}

      {isPaypalRetry && (
        <RetryPaymentPaypal
          sid={sid}
          currency={currency}
          amount={amount}
          orderId={orderId}
          siteId={siteId}
        />
      )}
      {isApplePayRetry && (
        <RetryPaymentApplePay
          sid={sid}
          currency={currency}
          customerId={customerId}
          amount={amount}
          orderId={orderId}
          siteId={siteId}
        />
      )}
      {isDefaultRetry && (
        <RetryPaymentDefault
          retryPaymentUrl={retryPaymentUrl}
          retryPaymentMethod={retryPaymentMethod}
          retryPaymentStatus={retryPaymentStatus}
          orderId={orderId}
          connector={connector}
          pspToken={pspToken}
          amount={amount}
          currency={currency}
          siteId={siteId}
          sid={sid}
          customerId={customerId}
          fraudPreventionRecommendation={fraudPreventionRecommendation}
        />
      )}
    </>
  );
};

export default RetryPaymentSwitch;
